import { combineReducers } from 'redux';
import { takeEvery, call } from 'redux-saga/effects';
import sortBy from 'lodash.sortby';
import get from 'lodash.get';
import request from '../api-utils/request';
import actionKey from '../utils/action-key';
import { UPDATE_SCORE } from './assessments';
import { SET_ME } from './me';
import { UPDATE_USER_POSITION } from './manage-position';
import mapScores from '../utils/map-scores';
import maxScores from '../../constants/max-scores';

export const FETCH_USER_SCORE = 'users/FETCH_USER_SCORE';
export const GENERATE_PDF_REPORT = 'users/GENERATE_PDF_REPORT';

export const usersList = (state = {}, action) => {
    switch (action.type) {
        case actionKey.success(FETCH_USER_SCORE): {
            const user = action.payload.user;
            const scores = action.payload.scores;
            return {
                ...state,
                [action.id]: {
                    ...state[action.id],
                    id: action.id,
                    ...user,
                    name: `${user.firstName} ${user.lastName}`,
                    scores: mapScores(scores),
                    valuesCompleted: scoreCompleted(scores, 'Achievement'),
                    interestsCompleted: scoreCompleted(scores, 'Realistic'),
                    workplaceCompleted: scoreCompleted(scores, 'Excellence'),
                    personalityCompleted: scoreCompleted(scores, 'Conscientiousness'),
                    purposeCompleted: scoreCompleted(scores, 'Living a Purpose'),
                },
            };
        }
        case SET_ME: {
            const user = action.payload.user;
            const scores = action.payload.scores;
            return {
                ...state,
                [user.userId]: {
                    id: user.userId,
                    ...user,
                    name: `${user.firstName} ${user.lastName}`,
                    scores: mapScores(scores),
                    valuesCompleted: scoreCompleted(scores, 'Achievement'),
                    interestsCompleted: scoreCompleted(scores, 'Realistic'),
                    workplaceCompleted: scoreCompleted(scores, 'Excellence'),
                    personalityCompleted: scoreCompleted(scores, 'Conscientiousness'),
                    purposeCompleted: scoreCompleted(scores, 'Living a Purpose'),
                },
            };
        }
        case actionKey.success(UPDATE_USER_POSITION): {
            return {
                ...state,
                [action.userId]: null,
            };
        }
        // reset score after assessment is done
        case UPDATE_SCORE: {
            const {
                assessment,
                scores,
                userId,
            } = action.payload;
            return {
                ...state,
                [userId]: {
                    ...(state[userId] || {}),
                    scores: {
                        ...get(state, `${userId}.scores`, {}),
                        [assessment]: mapScores(scores)[assessment],
                    },
                    [`${assessment}Completed`]: true,
                },
            };
        }
        default:
            return state;
    }
};

export const scoreCompleted = (scores, name) =>
    scores && typeof scores[name] !== 'undefined';

export const loadingScores = (state = false, action) => {
    switch (action.type) {
        case FETCH_USER_SCORE:
            return true;
        case actionKey.success(FETCH_USER_SCORE):
        case actionKey.failure(FETCH_USER_SCORE):
            return false;
        default:
            return state;
    }
};

export const error = (state = null, action) => {
    switch (action.type) {
        case FETCH_USER_SCORE:
        case actionKey.success(FETCH_USER_SCORE):
            return null;
        case actionKey.failure(FETCH_USER_SCORE):
            return action.error;
        default:
            return state;
    }
};


export default combineReducers({
    error,
    loadingScores,
    list: usersList,
});

/**
 * REDUX ACTIONS
 */

export const fetchUserScoreAction = id => ({
    type: FETCH_USER_SCORE,
    id,
});

export const generateProfilePdfAction = ({ userId, scope, resolve, reject }) => ({
    type: GENERATE_PDF_REPORT,
    userId,
    resolve,
    reject,
    scope,
});

/**
 * SAGA LISTENERS
 */

export function* listenFetchUserScore() {
    yield takeEvery(FETCH_USER_SCORE, fetchUserScore);
}

export function* listenGeneratePDF() {
    yield takeEvery(GENERATE_PDF_REPORT, generateProfilePdf);
}

export function* rootSaga() {
    yield [
        listenFetchUserScore(),
        listenGeneratePDF(),
    ];
}

export function* fetchUserScore({ id, type }) {
    yield call(request, {
        url: `/api/users/score/${id}`,
        type,
        id,
    });
}
export function* generateProfilePdf({ type, userId, scope, resolve, reject }) {
    const result = yield call(request, {
        type,
        url: '/api/users/generate-profile-pdf',
        options: {
            method: 'POST',
            body: {
                userId,
                scope,
            },
        },
    });
    if (!result || result.error) {
        reject(result.error);
    } else {
        resolve(result.payload.file);
    }
}

const getPercentage = ({ min, max, value }) =>
    Math.max(0, Math.round(((value - min) * 100) / (max - min)));

export const convertScoreToPercentage = ({ assessment, dimension, score }) => {
    // Range of Personality values: 1.00 - 5.00
    // Range of Interests values: 0.00 - 40.00
    // Range of WPP values: 1.00 - 5.00
    // values
    // Working Conditions Range: 8.00 - 28.00
    // Achievement/Independence/Recognition/Relationships/Support 6.00 - 30.00
    switch (assessment) {
        case 'workplace':
        case 'personality':
            return getPercentage({
                min: 1,
                max: maxScores[assessment],
                value: score,
            });
        case 'interests':
            return getPercentage({
                min: 0,
                max: maxScores[assessment],
                value: score,
            });
        case 'purpose': {
            if (dimension === 'Living a Purpose') {
                return getPercentage({
                    min: 5,
                    max: 20,
                    value: score,
                });
            }
            return getPercentage({
                min: 4,
                max: 16,
                value: score,
            });
        }
        // values
        default:
            if (dimension === 'Working Conditions') {
                return getPercentage({
                    min: 8,
                    max: 28,
                    value: score,
                });
            }
            return getPercentage({
                min: 6,
                max: maxScores[assessment],
                value: score,
            });
    }
};


export const getScoreDescription = (score) => {
    if (score <= 20) {
        return 'Score: Very Low';
    }
    if (score <= 40) {
        return 'Score: Low';
    }
    if (score <= 60) {
        return 'Score: Medium';
    }
    if (score <= 80) {
        return 'Score: High';
    }
    return 'Score: Very High';
};

export const interestsProfile = (_scores) => {
    // If there are more than 3 scores within 5 points of the high score,
    // the user has what is called a “flat profile.”
    // There are 3 types of flat profiles:
    // If the group of high scores are all higher than 25. These will be called HIGH FLAT
    // If the group of high scores are between 16-25. These will be called MID FLAT
    // If the group of high scores are less than 15. These will be called LOW FLAT
    const scores = {};
    Object.keys(_scores).forEach((key) => {
        scores[key] = Number(_scores[key]);
    });
    let highScore = Object.keys(scores)[0];
    Object.keys(scores).forEach((key) => {
        if (scores[key] > scores[highScore]) {
            highScore = key;
        }
    });
    const high = scores[highScore];
    let range = [high];
    Object.keys(scores).forEach((key) => {
        if (key === highScore) {
            return;
        }
        if (scores[key] >= high - 5) {
            range.push(scores[key]);
        }
    });
    if (range.length > 3) {
        // high profile
        range = sortBy(range).splice(-3); // take last 3

        const sum = range.reduce((acc, next) => (acc + next), 0);
        const avg = sum / 3;
        if (avg > 25) {
            return 'high-flat';
        } else if (avg >= 16) {
            return 'mid-flat';
        }
        return 'low-flat';
    } else if (high < 20) {
        return 'low-scores';
    }

    return 'normal';
};




// WEBPACK FOOTER //
// ./src/client/redux/users.js